/**
 * First-run home tour — spotlight + popover coachmarks.
 *
 * Source of truth for "has the user finished this?" is server-side
 * (`me.tour_completed`, persisted as `users.tour_completed_at`). The
 * localStorage flag below is a fast short-circuit so we don't have to
 * peek into `me` on every home paint after the tour is done.
 *
 * Behaviour:
 *   - `maybeStartHomeTour({ me })` is the home-page entry point: it bails
 *     instantly if either the local flag or the server flag says we're done.
 *   - Finish AND Skip both POST /me/tour/complete (idempotent on the server).
 *   - `forceStartHomeTour()` is the "Replay tour" entry point from /profile.
 *   - Missing anchors are skipped gracefully so a brand-new user with no
 *     pinned trivia / no previous trivia still sees a coherent tour.
 *
 * Lifecycle: only one tour can run at a time; the engine refuses to start
 * a second one. Closing detaches every listener it installed.
 */

import { api } from '../api.js';
import { auth } from '../auth.js';

const LOCAL_DONE_KEY = 'triviana.tour_done_v1';
// Cross-page handoff: profile sets this, navigates to /, home consumes it
// inside paintLoaded so the tour starts only after anchors are mounted.
const REPLAY_REQUEST_KEY = 'triviana.tour_replay_request';

// Phase E — the power-ups + notifications tour shares the same engine
// but persists to a different column / localStorage key. Same shape so
// the two tours can run in sequence (first one finishes → tour_completed
// flips true → next /home load lands on the power-ups tour).
const LOCAL_POWERUPS_DONE_KEY = 'triviana.powerups_tour_done_v1';
const POWERUPS_REPLAY_REQUEST_KEY = 'triviana.powerups_tour_replay_request';

// Steps are anchored via `data-tour="key"`. Order matters; the tour walks
// them top-to-bottom. Keep copy short — these float over the page, not full
// modals. Each step's `selector` is queried at step time, so anchors that
// only render some days (pinned live trivia, previous live) skip cleanly.
const HOME_STEPS = [
  {
    key: 'live-hero',
    selector: '[data-tour="live-hero"]',
    title: 'Featured live trivia',
    body: 'Big prize pools, scheduled rooms. Tap Join to lock your seat — we\'ll skip this if nothing\'s scheduled today.',
  },
  {
    key: 'game-modes',
    selector: '[data-tour="game-modes"]',
    title: 'Pick a game mode',
    body: 'Solo to practice, 1-on-1 to challenge a real player, or Group to play with friends.',
  },
  {
    key: 'duels-strip',
    selector: '[data-tour="duels-strip"]',
    title: '1-v-1 duels',
    body: 'Head-to-head challenges — publish a duel or join an open one. Winner takes the pot.',
  },
  {
    key: 'live-list',
    selector: '[data-tour="live-list"]',
    title: 'Live & upcoming',
    body: 'Every scheduled and live trivia shows up here. Tap "See all live trivias" to browse the lot.',
  },
  {
    key: 'previous-live',
    selector: '[data-tour="previous-live"]',
    title: 'Your last live trivia',
    body: 'After your first live trivia, your most recent rank, score, and payout land here.',
  },
  {
    key: 'wallet-tab',
    selector: '[data-tour="wallet-tab"]',
    title: 'Your wallet',
    body: 'Top up, withdraw, and see your balance from the Wallet tab.',
  },
  {
    key: 'eraser-hud',
    selector: '[data-tour="eraser-hud"]',
    title: 'Erasers',
    body: 'Erasers remove a wrong answer in live trivia. Tap to buy or check your stash.',
  },
];

// Phase E tour — power-ups deep-dive + notifications. Plays on the
// SECOND /home load for new users (after the first tour set
// tour_completed=true) and on the next /home load for existing users.
// Three steps; copy intentionally short — these float over the page.
const POWERUPS_STEPS = [
  {
    key: 'pu-eraser-chip',
    selector: '[data-tour="powerups-eraser"]',
    title: 'Auto-fix mistakes',
    body: 'Erasers wipe a wrong answer in live trivia. You’ve got 1 to spend.',
  },
  {
    key: 'pu-fiftyfifty-chip',
    selector: '[data-tour="powerups-fiftyfifty"]',
    title: 'Two wrong options gone',
    body: 'Tap Use 50/50 mid-game to drop two wrong answers. You’ve got 1 to start.',
  },
  {
    key: 'pu-notif-bell',
    selector: '[data-tour="notif-bell"]',
    title: 'Don’t miss the next live',
    body: 'We ping you when live trivia opens. Tap the bell to manage which alerts you get.',
  },
];

// Module-level guard so a stray double-call (e.g. /home polling + auth
// re-render landing on the same paint) can't stack two overlays.
let _activeTour = null;

function localFlagSet(key = LOCAL_DONE_KEY) {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

function setLocalFlag(key = LOCAL_DONE_KEY) {
  try { localStorage.setItem(key, '1'); } catch { /* ignore */ }
}

function clearLocalFlag(key = LOCAL_DONE_KEY) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Home-page entry point. Call from the success path of `refreshAndPaint`
 * after `auth.setMe(payload.me)`. Returns immediately for users who've
 * already taken (or skipped) the tour.
 *
 * Also consumes a pending replay request from `/profile` — if the user
 * clicked "Replay tour" we kick off the tour even if `me.tour_completed`
 * was true (the reset POST may not have landed yet from this device).
 */
export function maybeStartHomeTour({ me } = {}) {
  _maybeStartTour({
    me,
    steps: HOME_STEPS,
    localKey: LOCAL_DONE_KEY,
    replayKey: REPLAY_REQUEST_KEY,
    persistEndpoint: '/me/tour/complete',
    meFlag: 'tour_completed',
  });
}

/**
 * Phase E entry point. Plays the power-ups + notifications tour when:
 *   - the user has finished the first tour (me.tour_completed = true), AND
 *   - they have not yet completed this one (me.powerups_tour_completed = false),
 * AND no other tour is currently running. New users hit the gate on
 * their second /home load; existing users hit it on their next visit.
 */
export function maybeStartPowerupsTour({ me } = {}) {
  // Sequencing rule: only start when the first tour is done. We don't
  // want to stack two coachmarks on a fresh user's very first load.
  // The replay path is an exception — the user explicitly asked for it.
  const replayRequested = _peekReplayRequested(POWERUPS_REPLAY_REQUEST_KEY);
  if (!replayRequested && me && !me.tour_completed) return;
  _maybeStartTour({
    me,
    steps: POWERUPS_STEPS,
    localKey: LOCAL_POWERUPS_DONE_KEY,
    replayKey: POWERUPS_REPLAY_REQUEST_KEY,
    persistEndpoint: '/me/powerups-tour/complete',
    meFlag: 'powerups_tour_completed',
  });
}

function _maybeStartTour({ me, steps, localKey, replayKey, persistEndpoint, meFlag }) {
  if (_activeTour) return;
  const replayRequested = consumeReplayRequest(replayKey);
  if (replayRequested) {
    // Profile button cleared the local flag + issued the server reset.
    // Run the tour now that anchors are in the DOM.
    requestAnimationFrame(() => startTour(steps, { localKey, persistEndpoint }));
    return;
  }
  if (localFlagSet(localKey)) return;
  if (!me) return;
  if (me[meFlag]) {
    setLocalFlag(localKey);
    return;
  }
  // Defer one frame so the freshly-painted DOM has settled.
  requestAnimationFrame(() => startTour(steps, { localKey, persistEndpoint }));
}

/**
 * Replay entry point (from a "Replay tour" link on `/profile`). Clears the
 * server + local flags and stashes a session-scoped "please start the tour
 * once /home renders" flag. The caller is responsible for navigating to /.
 * Splitting the work this way avoids racing the home-page's own paint.
 */
export async function requestHomeTourReplay() {
  return _requestReplay({
    localKey: LOCAL_DONE_KEY,
    replayKey: REPLAY_REQUEST_KEY,
    resetEndpoint: '/me/tour/reset',
  });
}

/** Phase E "Replay power-ups tour" entry point. Same flow as the first. */
export async function requestPowerupsTourReplay() {
  return _requestReplay({
    localKey: LOCAL_POWERUPS_DONE_KEY,
    replayKey: POWERUPS_REPLAY_REQUEST_KEY,
    resetEndpoint: '/me/powerups-tour/reset',
  });
}

async function _requestReplay({ localKey, replayKey, resetEndpoint }) {
  clearLocalFlag(localKey);
  try {
    sessionStorage.setItem(replayKey, '1');
  } catch { /* ignore — non-fatal, the user can still retry */ }
  try {
    const me = await api(resetEndpoint, { method: 'POST' });
    auth.setMe(me);
  } catch {
    // Tour will still play locally; server state catches up on next /me.
  }
}

function consumeReplayRequest(key = REPLAY_REQUEST_KEY) {
  try {
    if (sessionStorage.getItem(key) !== '1') return false;
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function _peekReplayRequested(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

function startTour(steps, { localKey = LOCAL_DONE_KEY, persistEndpoint = '/me/tour/complete' } = {}) {
  if (_activeTour) return;
  // Resolve anchors lazily at step-time, but pre-filter empty steps so the
  // progress indicator ("Step 2 of 5") is honest.
  const live = steps.filter((s) => !!document.querySelector(s.selector));
  if (live.length === 0) {
    // Nothing to point at — mark complete so we don't pester them.
    void persistComplete(persistEndpoint);
    return;
  }

  const state = {
    steps: live,
    index: 0,
    overlayEl: null,
    spotlightEl: null,
    popoverEl: null,
    onKeydown: null,
    onResize: null,
    onScroll: null,
    lastFocus: document.activeElement,
    localKey,
    persistEndpoint,
  };
  _activeTour = state;

  state.overlayEl = document.createElement('div');
  state.overlayEl.className = 'tour-overlay';
  state.overlayEl.setAttribute('role', 'dialog');
  state.overlayEl.setAttribute('aria-modal', 'true');
  state.overlayEl.setAttribute('aria-label', 'App tour');
  state.overlayEl.innerHTML = `
    <div class="tour-backdrop" data-tour-skip></div>
    <div class="tour-spotlight" aria-hidden="true"></div>
    <div class="tour-popover" tabindex="-1"></div>
  `;
  document.body.appendChild(state.overlayEl);
  state.spotlightEl = state.overlayEl.querySelector('.tour-spotlight');
  state.popoverEl = state.overlayEl.querySelector('.tour-popover');

  // Backdrop click = skip (treat as taken so we don't re-nag).
  state.overlayEl.querySelector('[data-tour-skip]').addEventListener('click', () => {
    closeTour({ markDone: true });
  });

  state.onKeydown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closeTour({ markDone: true }); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); return; }
    if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); return; }
    if (e.key === 'Tab') {
      // Trap focus inside the popover so a stray Tab doesn't escape
      // behind the backdrop and land on a now-invisible button.
      trapTab(e, state.popoverEl);
    }
  };
  document.addEventListener('keydown', state.onKeydown, true);

  // Reposition on viewport change. RAF-debounced so a rapid resize doesn't
  // hammer the layout thread.
  let rafId = null;
  const requestRender = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => { rafId = null; render(); });
  };
  state.onResize = requestRender;
  state.onScroll = requestRender;
  window.addEventListener('resize', state.onResize);
  window.addEventListener('scroll', state.onScroll, true);

  enterStep();

  // Called once per step transition (initial + next + prev). Handles the
  // scroll-into-view so a section below the fold comes on-screen before the
  // popover appears. The scroll listener already keeps `placeSpotlight`
  // aligned as the smooth scroll animates, so `render()` stays a pure
  // reposition and doesn't re-trigger scrolling on every frame — otherwise
  // the tour would fight the user if they manually scrolled away.
  function enterStep() {
    if (!_activeTour) return;
    const step = state.steps[state.index];
    const anchor = step ? document.querySelector(step.selector) : null;
    if (anchor) {
      // block: 'center' keeps the anchor and popover both comfortably
      // in-view on the same paint (the popover sits directly below the
      // anchor by default). No-op if already visible.
      try {
        anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch { /* older browsers ignore the options object silently */ }
    }
    render();
  }

  function render() {
    if (!_activeTour) return;
    const step = state.steps[state.index];
    const anchor = step ? document.querySelector(step.selector) : null;
    if (!anchor) {
      // Anchor vanished between paint and now (route change, slow render).
      // Advance until we find one or fall off the end.
      if (state.index < state.steps.length - 1) {
        state.index += 1;
        enterStep();
      } else {
        closeTour({ markDone: true });
      }
      return;
    }
    placeSpotlight(anchor);
    renderPopover(step, anchor);
  }

  function placeSpotlight(anchor) {
    const r = anchor.getBoundingClientRect();
    const pad = 8;
    const top = Math.max(0, r.top - pad);
    const left = Math.max(0, r.left - pad);
    const width = r.width + pad * 2;
    const height = r.height + pad * 2;
    Object.assign(state.spotlightEl.style, {
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${height}px`,
    });
  }

  function renderPopover(step, anchor) {
    const total = state.steps.length;
    const isLast = state.index === total - 1;
    state.popoverEl.innerHTML = `
      <div class="tour-pop-progress">Step ${state.index + 1} of ${total}</div>
      <div class="tour-pop-title">${escape(step.title)}</div>
      <div class="tour-pop-body">${escape(step.body)}</div>
      <div class="tour-pop-actions">
        <button type="button" class="tour-btn ghost" data-tour-action="skip">Skip tour</button>
        <div class="tour-pop-spacer"></div>
        ${state.index > 0
          ? `<button type="button" class="tour-btn ghost" data-tour-action="prev">Back</button>`
          : ''}
        <button type="button" class="tour-btn primary" data-tour-action="${isLast ? 'finish' : 'next'}">
          ${isLast ? 'Finish' : 'Next'}
        </button>
      </div>
    `;
    state.popoverEl.querySelectorAll('[data-tour-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const a = btn.dataset.tourAction;
        if (a === 'skip' || a === 'finish') closeTour({ markDone: true });
        else if (a === 'prev') prev();
        else if (a === 'next') next();
      });
    });
    positionPopover(anchor);
    // Focus the popover so screen readers announce the new step + keyboard
    // users can immediately Tab through the buttons.
    state.popoverEl.focus();
  }

  function positionPopover(anchor) {
    const ar = anchor.getBoundingClientRect();
    // Measure after content swap so width/height are honest.
    state.popoverEl.style.visibility = 'hidden';
    state.popoverEl.style.left = '0px';
    state.popoverEl.style.top = '0px';
    const pr = state.popoverEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 12;
    const spaceBelow = vh - ar.bottom;
    const spaceAbove = ar.top;
    // Prefer below; flip above only if below has no room and above does.
    const placeAbove = spaceBelow < pr.height + margin + 8 && spaceAbove > spaceBelow;
    let top = placeAbove
      ? Math.max(margin, ar.top - pr.height - margin)
      : Math.min(vh - pr.height - margin, ar.bottom + margin);
    // Horizontal: center under/over the anchor, clamp to viewport.
    let left = ar.left + ar.width / 2 - pr.width / 2;
    left = Math.max(margin, Math.min(left, vw - pr.width - margin));
    Object.assign(state.popoverEl.style, {
      top: `${Math.max(margin, top)}px`,
      left: `${left}px`,
      visibility: 'visible',
    });
    state.popoverEl.classList.toggle('above', placeAbove);
    state.popoverEl.classList.toggle('below', !placeAbove);
  }

  function next() {
    if (!_activeTour) return;
    if (state.index >= state.steps.length - 1) {
      closeTour({ markDone: true });
      return;
    }
    state.index += 1;
    enterStep();
  }

  function prev() {
    if (!_activeTour) return;
    if (state.index === 0) return;
    state.index -= 1;
    enterStep();
  }
}

function closeTour({ markDone }) {
  const state = _activeTour;
  if (!state) return;
  _activeTour = null;
  document.removeEventListener('keydown', state.onKeydown, true);
  window.removeEventListener('resize', state.onResize);
  window.removeEventListener('scroll', state.onScroll, true);
  state.overlayEl?.remove();
  // Restore focus to whatever had it before the tour opened.
  if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
    try { state.lastFocus.focus(); } catch { /* ignore */ }
  }
  if (markDone) {
    setLocalFlag(state.localKey);
    void persistComplete(state.persistEndpoint);
  }
}

/**
 * Best-effort POST to the tour-complete endpoint. If it fails
 * (offline, 5xx) the local flag stays set so we don't re-nag — worst
 * case the user sees the tour again after a localStorage wipe or on a
 * brand-new device. The endpoint is idempotent so a later retry is
 * safe.
 */
async function persistComplete(endpoint = '/me/tour/complete') {
  try {
    const me = await api(endpoint, { method: 'POST' });
    auth.setMe(me);
  } catch {
    // Silent — the tour is over, no point disrupting the user. The next
    // /me fetch will refresh state and reconcile if needed.
  }
}

function trapTab(e, container) {
  const focusables = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

// Tiny escaper local to the module so this file has zero internal imports
// beyond api/auth — keeps it loadable from any page that wants to start
// the tour, without a tangle of mutual deps.
function escape(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
